import { createApi } from '@reduxjs/toolkit/query/react';
import type { BudgetType } from '@types';
import { keepUnusedDataFor } from '../constants/appActions';
import { baseQueryWithReauth } from '../utils/prepHeaders';

const TAG = 'Budget';

const budgetProjectTags = (project: number) => [
  { type: TAG, id: `summary-${project}` } as const,
  { type: 'BudgetCOImpact', id: `project-${project}` } as const,
  { type: 'BudgetStatistics', id: `project-${project}` } as const,
  { type: 'BudgetCompare', id: `project-${project}` } as const,
  { type: 'BudgetVersions', id: `project-${project}` } as const,
  { type: 'BudgetLines', id: `project-${project}` } as const,
  { type: 'BudgetActivity', id: `project-${project}` } as const,
];

export const budgetApi = createApi({
  reducerPath: 'budgetApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    TAG,
    'BudgetLines',
    'BudgetVersions',
    'BudgetActivity',
    'BudgetCOImpact',
    'BudgetImports',
    'BudgetStatistics',
    'BudgetSections',
    'BudgetCompare',
  ],
  endpoints: (builder) => ({
    // ---------------- Sections ----------------
    getBudgetSections: builder.query<
      BudgetType.Section[],
      { active?: boolean } | void
    >({
      query: (arg) => ({
        url: '/project-management/budget/sections/',
        method: 'GET',
        params: arg && arg.active ? { active: 1 } : undefined,
      }),
      keepUnusedDataFor,
      providesTags: () => [{ type: 'BudgetSections', id: 'all' }],
    }),

    // ---------------- Summary ----------------
    getBudgetSummary: builder.query<
      BudgetType.BudgetSummary,
      { project: number }
    >({
      query: ({ project }) => ({
        url: '/project-management/budget/',
        method: 'GET',
        params: { project },
      }),
      keepUnusedDataFor,
      providesTags: (_r, _e, arg) => [
        { type: TAG, id: `summary-${arg.project}` },
      ],
    }),

    createBudget: builder.mutation<
      BudgetType.BudgetSummary,
      { project: number }
    >({
      query: ({ project }) => ({
        url: '/project-management/budget/',
        method: 'POST',
        params: { project },
        body: { project },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: TAG, id: `summary-${arg.project}` },
        { type: 'BudgetVersions', id: `project-${arg.project}` },
      ],
    }),

    recalculateBudget: builder.mutation<
      BudgetType.BudgetSummary,
      { project: number }
    >({
      query: ({ project }) => ({
        url: '/project-management/budget/recalculate/',
        method: 'POST',
        params: { project },
        body: {},
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: TAG, id: `summary-${arg.project}` },
        { type: 'BudgetActivity', id: `project-${arg.project}` },
      ],
    }),

    exportBudgetPdf: builder.mutation<Blob, { project: number }>({
      query: ({ project }) => ({
        url: '/project-management/budget/export/',
        method: 'GET',
        params: { project },
        responseHandler: (response: Response) => response.blob(),
        validateStatus: (response) => response.status === 200,
      }),
    }),

    // ---------------- Lines ----------------
    getBudgetLines: builder.query<
      BudgetType.BudgetLine[],
      { project: number; section?: string; q?: string; ordering?: string }
    >({
      query: ({ project, section, q, ordering }) => ({
        url: '/project-management/budget/lines/',
        method: 'GET',
        params: { project, section, q, ordering },
      }),
      keepUnusedDataFor,
      providesTags: (_r, _e, arg) => [
        { type: 'BudgetLines', id: `project-${arg.project}` },
        {
          type: 'BudgetLines',
          id: `project-${arg.project}-${arg.section ?? 'all'}`,
        },
      ],
    }),

    createBudgetLine: builder.mutation<
      BudgetType.BudgetLine,
      { project: number; body: BudgetType.LineWriteInput }
    >({
      query: ({ project, body }) => ({
        url: '/project-management/budget/lines/',
        method: 'POST',
        params: { project },
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'BudgetLines', id: `project-${arg.project}-all` },
        { type: 'BudgetLines', id: `project-${arg.project}-FIELD` },
        { type: 'BudgetLines', id: `project-${arg.project}-LABOUR` },
        { type: TAG, id: `summary-${arg.project}` },
        { type: 'BudgetActivity', id: `project-${arg.project}` },
      ],
    }),

    patchBudgetLine: builder.mutation<
      BudgetType.BudgetLine,
      {
        project: number;
        lineId: number;
        body: Partial<BudgetType.LineWriteInput>;
      }
    >({
      query: ({ lineId, body }) => ({
        url: `/project-management/budget/lines/${lineId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'BudgetLines', id: `project-${arg.project}-all` },
        { type: 'BudgetLines', id: `project-${arg.project}-FIELD` },
        { type: 'BudgetLines', id: `project-${arg.project}-LABOUR` },
        { type: TAG, id: `summary-${arg.project}` },
        { type: 'BudgetActivity', id: `project-${arg.project}` },
      ],
    }),

    deleteBudgetLine: builder.mutation<
      void,
      { project: number; lineId: number }
    >({
      query: ({ lineId }) => ({
        url: `/project-management/budget/lines/${lineId}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'BudgetLines', id: `project-${arg.project}-all` },
        { type: 'BudgetLines', id: `project-${arg.project}-FIELD` },
        { type: 'BudgetLines', id: `project-${arg.project}-LABOUR` },
        { type: TAG, id: `summary-${arg.project}` },
        { type: 'BudgetActivity', id: `project-${arg.project}` },
      ],
    }),

    bulkUpdateBudgetLines: builder.mutation<
      {
        created: BudgetType.BudgetLine[];
        updated: BudgetType.BudgetLine[];
        deleted_count: number;
        summary: BudgetType.BudgetSummary;
      },
      {
        project: number;
        body: {
          creates?: BudgetType.LineWriteInput[];
          updates?: Array<Partial<BudgetType.LineWriteInput> & { id: number }>;
          deletes?: number[];
        };
      }
    >({
      query: ({ project, body }) => ({
        url: '/project-management/budget/lines/bulk/',
        method: 'POST',
        params: { project },
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'BudgetLines', id: `project-${arg.project}-all` },
        { type: 'BudgetLines', id: `project-${arg.project}-FIELD` },
        { type: 'BudgetLines', id: `project-${arg.project}-LABOUR` },
        { type: TAG, id: `summary-${arg.project}` },
        { type: 'BudgetActivity', id: `project-${arg.project}` },
      ],
    }),

    // ---------------- Versions / history ----------------
    createBudgetSnapshot: builder.mutation<
      BudgetType.BudgetVersion,
      { project: number; body: { label?: string; reason?: string } }
    >({
      query: ({ project, body }) => ({
        url: '/project-management/budget/snapshot/',
        method: 'POST',
        params: { project },
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'BudgetVersions', id: `project-${arg.project}` },
        { type: TAG, id: `summary-${arg.project}` },
        { type: 'BudgetActivity', id: `project-${arg.project}` },
      ],
    }),

    getBudgetVersions: builder.query<
      BudgetType.BudgetVersion[],
      { project: number }
    >({
      query: ({ project }) => ({
        url: '/project-management/budget/versions/',
        method: 'GET',
        params: { project },
      }),
      keepUnusedDataFor,
      providesTags: (_r, _e, arg) => [
        { type: 'BudgetVersions', id: `project-${arg.project}` },
      ],
    }),

    getBudgetVersionDetail: builder.query<
      BudgetType.BudgetVersionDetail,
      { versionId: number }
    >({
      query: ({ versionId }) => ({
        url: `/project-management/budget/versions/${versionId}/`,
        method: 'GET',
      }),
      keepUnusedDataFor,
      providesTags: (_r, _e, arg) => [
        { type: 'BudgetVersions', id: `version-${arg.versionId}` },
      ],
    }),

    compareBudgetVersions: builder.query<
      BudgetType.CompareResponse,
      { project: number; from: number; to: number }
    >({
      query: ({ project, from, to }) => ({
        url: '/project-management/budget/compare/',
        method: 'GET',
        params: { project, from, to },
      }),
      keepUnusedDataFor: 60,
      providesTags: (_r, _e, arg) => [
        { type: 'BudgetCompare', id: `project-${arg.project}` },
      ],
    }),

    getBudgetAsOf: builder.query<
      BudgetType.BudgetVersionDetail,
      { project: number; date: string }
    >({
      query: ({ project, date }) => ({
        url: '/project-management/budget/as-of/',
        method: 'GET',
        params: { project, date },
      }),
      keepUnusedDataFor: 30,
    }),

    getBudgetActivity: builder.query<
      BudgetType.BudgetActivity[],
      { project: number; line?: number; type?: string }
    >({
      query: ({ project, line, type }) => ({
        url: '/project-management/budget/activity/',
        method: 'GET',
        params: { project, line, type },
      }),
      keepUnusedDataFor: 30,
      providesTags: (_r, _e, arg) => [
        { type: 'BudgetActivity', id: `project-${arg.project}` },
      ],
    }),

    // ---------------- Change Order ↔ Budget ----------------
    getBudgetChangeOrders: builder.query<
      BudgetType.COImpactResponse,
      { project: number }
    >({
      query: ({ project }) => ({
        url: '/project-management/budget/change-orders/',
        method: 'GET',
        params: { project },
      }),
      keepUnusedDataFor: 30,
      providesTags: (_r, _e, arg) => [
        { type: 'BudgetCOImpact', id: `project-${arg.project}` },
      ],
    }),

    allocateChangeOrder: builder.mutation<
      {
        version_id: number;
        version_number: number;
        allocations: Array<{
          id: number;
          change_order: number;
          budget: number;
          budget_line: number | null;
          line_section_code: string;
          line_cost_code: string;
          line_cost_code_name: string;
          amount: string;
          note: string;
        }>;
        all_profit: boolean;
        split_note: string;
        changed: boolean;
      },
      { project: number; cpId: number; body: BudgetType.AllocationRequest }
    >({
      query: ({ cpId, body }) => ({
        url: `/project-management/budget/change-orders/${cpId}/allocate/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        ...budgetProjectTags(arg.project),
        { type: 'BudgetLines', id: `project-${arg.project}-all` },
        { type: 'BudgetLines', id: `project-${arg.project}-FIELD` },
        { type: 'BudgetLines', id: `project-${arg.project}-LABOUR` },
      ],
    }),

    deleteAllocation: builder.mutation<
      void,
      { project: number; cpId: number; allocationId: number }
    >({
      query: ({ cpId, allocationId }) => ({
        url: `/project-management/budget/change-orders/${cpId}/allocations/${allocationId}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
      invalidatesTags: (_r, _e, arg) => [
        ...budgetProjectTags(arg.project),
        { type: 'BudgetLines', id: `project-${arg.project}-all` },
        { type: 'BudgetLines', id: `project-${arg.project}-FIELD` },
        { type: 'BudgetLines', id: `project-${arg.project}-LABOUR` },
      ],
    }),

    /** Tag a CO to a budget line (no money movement — works for any CO status).
     *  Pass line_id=null to clear the tag from all lines. */
    tagChangeOrderLine: builder.mutation<
      | { line_id: number; cost_code: string; cp_number: string }
      | { cleared: number },
      {
        project: number;
        cpId: number;
        line_id?: number | null;
        clear_line_id?: number | null;
      }
    >({
      query: ({ project, cpId, line_id, clear_line_id }) => ({
        url: `/project-management/budget/change-orders/${cpId}/tag-line/`,
        method: 'POST',
        params: { project },
        body: { project, line_id, clear_line_id },
      }),
      invalidatesTags: (_r, _e, arg) => [
        ...budgetProjectTags(arg.project),
        { type: 'BudgetLines', id: `project-${arg.project}-all` },
        { type: 'BudgetLines', id: `project-${arg.project}-FIELD` },
        { type: 'BudgetLines', id: `project-${arg.project}-LABOUR` },
      ],
    }),

    // ---------------- Imports ----------------
    registerBudgetImport: builder.mutation<
      BudgetType.BudgetImport,
      {
        project: number;
        body: {
          file_url: string;
          file_name: string;
          target_section?: number | null;
          column_mapping?: Record<string, string>;
        };
      }
    >({
      query: ({ project, body }) => ({
        url: '/project-management/budget/import/',
        method: 'POST',
        params: { project },
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'BudgetImports', id: `project-${arg.project}` },
        { type: 'BudgetActivity', id: `project-${arg.project}` },
      ],
    }),

    getBudgetImport: builder.query<BudgetType.BudgetImport, { id: number }>({
      query: ({ id }) => ({
        url: `/project-management/budget/import/${id}/`,
        method: 'GET',
      }),
      keepUnusedDataFor: 10,
    }),

    patchBudgetImport: builder.mutation<
      BudgetType.BudgetImport,
      {
        id: number;
        body: {
          target_section?: number | null;
          column_mapping?: Record<string, string>;
        };
      }
    >({
      query: ({ id, body }) => ({
        url: `/project-management/budget/import/${id}/`,
        method: 'PATCH',
        body,
      }),
    }),

    commitBudgetImport: builder.mutation<
      BudgetType.BudgetVersion,
      { project: number; id: number }
    >({
      query: ({ id }) => ({
        url: `/project-management/budget/import/${id}/commit/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'BudgetImports', id: `project-${arg.project}` },
        { type: 'BudgetLines', id: `project-${arg.project}-all` },
        { type: 'BudgetLines', id: `project-${arg.project}-FIELD` },
        { type: 'BudgetLines', id: `project-${arg.project}-LABOUR` },
        { type: TAG, id: `summary-${arg.project}` },
        { type: 'BudgetVersions', id: `project-${arg.project}` },
        { type: 'BudgetActivity', id: `project-${arg.project}` },
      ],
    }),

    // Mapping templates
    getMappingTemplates: builder.query<
      BudgetType.MappingTemplate[],
      { project?: number; scope?: string } | void
    >({
      query: (arg) => ({
        url: '/project-management/budget/import/templates/',
        method: 'GET',
        params: arg ? { ...arg } : undefined,
      }),
      keepUnusedDataFor,
    }),

    createMappingTemplate: builder.mutation<
      BudgetType.MappingTemplate,
      {
        body: {
          name: string;
          scope: 'global' | 'company' | 'project';
          project?: number | null;
          mapping: Record<string, string>;
        };
      }
    >({
      query: ({ body }) => ({
        url: '/project-management/budget/import/templates/',
        method: 'POST',
        body,
      }),
    }),

    deleteMappingTemplate: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/project-management/budget/import/templates/${id}/`,
        method: 'DELETE',
        validateStatus: (response) => response.status === 204,
      }),
    }),

    // Direct PUT to S3 (for the import file upload step)
    putBudgetImportToS3: builder.mutation<
      void,
      { file: File; content_type: string; s3_url: string }
    >({
      query: ({ file, content_type, s3_url }) => ({
        url: s3_url,
        method: 'PUT',
        headers: { 'Content-Type': content_type },
        body: file,
        validateStatus: (response) => response.status === 200,
      }),
    }),

    // ---------------- J/C Job Cost Code Detail Report — batch import ----------------
    /** Multi-file preview: parses every uploaded xlsx and returns the
     *  diff against the existing project budget. Read-only on
     *  ProjectBudgetLine. */
    batchPreviewBudgetImport: builder.mutation<
      BudgetType.JcBatchPreviewResponse,
      BudgetType.JcBatchPreviewRequest
    >({
      query: (body) => ({
        url: '/project-management/budget/import/preview/',
        method: 'POST',
        body,
      }),
    }),

    startBudgetImportPreviewJob: builder.mutation<
      BudgetType.JcPreviewJob,
      BudgetType.JcBatchPreviewRequest
    >({
      query: (body) => ({
        url: '/project-management/budget/import/preview/async/',
        method: 'POST',
        body,
      }),
    }),

    getBudgetImportPreviewJob: builder.query<
      BudgetType.JcPreviewJob,
      { jobId: string; project: number }
    >({
      query: ({ jobId, project }) => ({
        url: `/project-management/budget/import/preview/jobs/${jobId}/`,
        method: 'GET',
        params: { project },
      }),
      keepUnusedDataFor: 5,
    }),

    retryBudgetImportPreviewJob: builder.mutation<
      BudgetType.JcPreviewJob,
      { jobId: string; project: number }
    >({
      query: ({ jobId, project }) => ({
        url: `/project-management/budget/import/preview/jobs/${jobId}/retry/`,
        method: 'POST',
        body: { project },
      }),
    }),

    /** Multi-file commit. The user-confirmed per-row section is the
     *  source of truth — defaults are filled in the wizard from
     *  `suggested_section_id`. Atomic per request. */
    batchCommitBudgetImport: builder.mutation<
      BudgetType.JcBatchCommitResponse,
      { project: number; body: BudgetType.JcBatchCommitRequest }
    >({
      query: ({ body }) => ({
        url: '/project-management/budget/import/commit/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'BudgetImports', id: `project-${arg.project}` },
        { type: 'BudgetLines', id: `project-${arg.project}-all` },
        { type: 'BudgetLines', id: `project-${arg.project}-FIELD` },
        { type: 'BudgetLines', id: `project-${arg.project}-LABOUR` },
        { type: TAG, id: `summary-${arg.project}` },
        { type: 'BudgetVersions', id: `project-${arg.project}` },
        { type: 'BudgetActivity', id: `project-${arg.project}` },
        { type: 'BudgetStatistics', id: `project-${arg.project}` },
      ],
    }),

    // ---------------- Statistics ----------------
    getBudgetStatistics: builder.query<
      BudgetType.StatisticsResponse,
      { project: number }
    >({
      query: ({ project }) => ({
        url: '/project-management/budget/statistics/',
        method: 'GET',
        params: { project },
      }),
      keepUnusedDataFor: 30,
      providesTags: (_r, _e, arg) => [
        { type: 'BudgetStatistics', id: `project-${arg.project}` },
      ],
    }),
  }),
});

export const {
  useGetBudgetSectionsQuery,
  useGetBudgetSummaryQuery,
  useCreateBudgetMutation,
  useRecalculateBudgetMutation,
  useExportBudgetPdfMutation,
  useGetBudgetLinesQuery,
  useCreateBudgetLineMutation,
  usePatchBudgetLineMutation,
  useDeleteBudgetLineMutation,
  useBulkUpdateBudgetLinesMutation,
  useCreateBudgetSnapshotMutation,
  useGetBudgetVersionsQuery,
  useGetBudgetVersionDetailQuery,
  useCompareBudgetVersionsQuery,
  useGetBudgetAsOfQuery,
  useGetBudgetActivityQuery,
  useGetBudgetChangeOrdersQuery,
  useAllocateChangeOrderMutation,
  useDeleteAllocationMutation,
  useTagChangeOrderLineMutation,
  useRegisterBudgetImportMutation,
  useGetBudgetImportQuery,
  usePatchBudgetImportMutation,
  useCommitBudgetImportMutation,
  useGetMappingTemplatesQuery,
  useCreateMappingTemplateMutation,
  useDeleteMappingTemplateMutation,
  usePutBudgetImportToS3Mutation,
  useBatchPreviewBudgetImportMutation,
  useStartBudgetImportPreviewJobMutation,
  useLazyGetBudgetImportPreviewJobQuery,
  useRetryBudgetImportPreviewJobMutation,
  useBatchCommitBudgetImportMutation,
  useGetBudgetStatisticsQuery,
} = budgetApi;

export namespace BudgetApi {
  export const Api = budgetApi;
}
